import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { CarbonFootprintResult, HydricFootprintResult } from "@/types/results";
import { Scale, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

interface AllocationAnalysisCardProps {
  data: CarbonFootprintResult | HydricFootprintResult;
  className?: string;
}

export const AllocationAnalysisCard = ({ data, className }: AllocationAnalysisCardProps) => {
  const { t } = useLanguage();

  const {
    rd: decision_ratio,
    r_prod: physical_allocation_ratio,
    r_fat: economic_allocation_ratio,
    uF: functional_unit,
    U: unit,
    q: quantity_produced,
    fat_prod: annual_revenue,
  } = data;

  const isWaterFootprint = "ph_prod" in data && !("pc_prod" in data);
  const isPhysicalAllocation = decision_ratio <= 5;

  // Calculate efficiency metrics
  // Note: pc_prod and ph_prod now contain annual totals, so we need to calculate per-unit values
  const footprintPerUnit = "pc_prod" in data ? data.pc_prod / quantity_produced : data.ph_prod / quantity_produced;

  const footprintPerRevenue =
    "pc_prod" in data
      ? (data.pc_prod / annual_revenue) * 1000 // per 1000 EUR (k€)
      : (data.ph_prod / annual_revenue) * 1000;

  const revenuePerUnit = annual_revenue / quantity_produced;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-blue-600" />
          {t("results.allocation.title")}
        </CardTitle>
        <p className="text-sm text-gray-600">{t("results.allocation.description")}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Allocation Method Decision */}
        {isWaterFootprint ? (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-blue-800">{t("results.allocation.waterFootprintMethod")}</span>
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                {t("results.allocation.economic")}
              </Badge>
            </div>
            <div className="mt-2 text-xs text-blue-600">{t("results.allocation.waterAlwaysEconomic")}</div>
          </div>
        ) : (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-blue-800">{t("results.allocation.decisionRatio")}</span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-blue-700">{formatNumber(decision_ratio, 2)}</span>
                {decision_ratio <= 5 ? (
                  <TrendingDown className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-700">{t("results.allocation.selectedMethod")}:</span>
              <Badge
                variant={isPhysicalAllocation ? "default" : "secondary"}
                className={
                  isPhysicalAllocation
                    ? "bg-green-100 text-green-800 hover:bg-green-100"
                    : "bg-orange-100 text-orange-800 hover:bg-orange-100"
                }
              >
                {isPhysicalAllocation ? t("results.allocation.physical") : t("results.allocation.economic")}
              </Badge>
            </div>

            <div className="mt-2 text-xs text-blue-600">
              {isPhysicalAllocation ? t("results.allocation.physicalNote") : t("results.allocation.economicNote")}
            </div>
          </div>
        )}

        {/* Allocation Ratios */}
        {isWaterFootprint ? (
          <div className="grid grid-cols-1 gap-4">
            <div className="p-4 rounded-lg border bg-orange-50 border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t("results.allocation.economicRatio")}</span>
                <AlertCircle className="h-4 w-4 text-orange-600" />
              </div>
              <div className="text-2xl font-bold text-gray-800">{formatNumber(economic_allocation_ratio * 100)}%</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border ${
                isPhysicalAllocation ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t("results.allocation.physicalRatio")}</span>
                {isPhysicalAllocation && <AlertCircle className="h-4 w-4 text-green-600" />}
              </div>
              <div className="text-2xl font-bold text-gray-800">{formatNumber(physical_allocation_ratio * 100)}%</div>
            </div>

            <div
              className={`p-4 rounded-lg border ${
                !isPhysicalAllocation ? "bg-orange-50 border-orange-200" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t("results.allocation.economicRatio")}</span>
                {!isPhysicalAllocation && <AlertCircle className="h-4 w-4 text-orange-600" />}
              </div>
              <div className="text-2xl font-bold text-gray-800">{formatNumber(economic_allocation_ratio * 100)}%</div>
            </div>
          </div>
        )}

        {/* Production & Economic Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">{t("results.allocation.production")}</div>
            <div className="text-lg font-semibold">
              {formatNumber(quantity_produced)} {unit}
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">{t("results.allocation.revenue")}</div>
            <div className="text-lg font-semibold">€{formatNumber(annual_revenue)}</div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">{t("results.allocation.revenuePerUnit")}</div>
            <div className="text-lg font-semibold">
              €{formatNumber(revenuePerUnit)}/{unit}
            </div>
          </div>
        </div>

        {/* Efficiency Metrics */}
        <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
          <h4 className="text-sm font-medium text-teal-800 mb-3">{t("results.allocation.efficiencyMetrics")}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-teal-600 mb-1">{t("results.allocation.footprintPerUnit")}</div>
              <div className="text-lg font-semibold text-teal-700">
                {formatNumber(footprintPerUnit)}{" "}
                {"pc_prod" in data ? t("results.carbon.units.kgCO2e") : t("results.hydric.units.m3")}/{unit}
              </div>
            </div>

            <div>
              <div className="text-xs text-teal-600 mb-1">{t("results.allocation.footprintPerRevenue")}</div>
              <div className="text-lg font-semibold text-teal-700">
                {formatNumber(footprintPerRevenue)}{" "}
                {"pc_prod" in data ? t("results.carbon.units.kgCO2e") : t("results.hydric.units.m3")}/k€
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
